import { Link } from "react-router-dom";
import { Seo } from "../components/seo/Seo";
import { Breadcrumbs } from "../components/seo/Breadcrumbs";

const SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Course",
      "@id": "https://makershub.be/workshops/simon-says#course",
      name: "Simon Says memory-game — Arduino-workshop voor kinderen",
      description:
        "In 3 uur bouwt je kind een eigen Simon Says memory-game met Arduino, breadboard, LEDs, knoppen en buzzer. Werkend apparaat mee naar huis. Geen voorkennis nodig.",
      provider: {
        "@type": "EducationalOrganization",
        name: "Makers Hub",
        url: "https://makershub.be/",
      },
      educationalLevel: "Basisschool 5de-6de leerjaar",
      audience: {
        "@type": "EducationalAudience",
        educationalRole: "student",
        audienceType: "Kinderen 10-12 jaar",
      },
      teaches: [
        "Arduino programmeren",
        "Elektronische schakelingen bouwen",
        "Breadboard gebruik",
        "Logisch denken via code",
        "Debuggen — fout zoeken en oplossen",
      ],
      courseMode: "in-person",
      inLanguage: "nl-BE",
      offers: {
        "@type": "Offer",
        price: "30",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: "https://makershub.be/workshops/simon-says",
        category: "Workshop kinderen",
        validFrom: "2026-04-01",
      },
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "in-person",
        courseWorkload: "PT3H",
        location: {
          "@type": "Place",
          name: "Klaslokaal in Vlaanderen — locatie meegedeeld bij inschrijving",
          address: {
            "@type": "PostalAddress",
            addressCountry: "BE",
            addressRegion: "Vlaanderen",
          },
        },
        instructor: {
          "@type": "Person",
          name: "Tuur Vanholen",
          url: "https://makershub.be/over",
        },
        maximumAttendeeCapacity: 8,
      },
    },
  ],
};

export function WorkshopSimonSaysPage() {
  return (
    <>
      <Seo
        title="Simon Says memory-game — Arduino-workshop kinderen 10-12 | Makers Hub"
        description="In 3 uur bouwt je kind een werkende Simon Says memory-game met Arduino. €30 all-in pilot-prijs, max 8 kinderen, alle componenten inbegrepen, werkend apparaat mee naar huis."
        path="/workshops/simon-says"
        schema={SCHEMA}
      />
      <article className="page page-workshop">
        <div className="container">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Workshops", to: "/workshops" },
              { label: "Simon Says memory-game" },
            ]}
          />

          <header className="page-hero">
            <span className="section-tag">Workshop · 1 zaterdag · 3u</span>
            <h1>
              Simon Says <span className="accent">memory-game</span>
            </h1>
            <p className="lead">
              In 3 uur bouwt je kind zijn eigen Simon Says memory-game — vier knoppen, vier
              kleuren, oplopende patronen. Echte Arduino, echte componenten, werkend apparaat mee
              naar huis. €30 all-in (pilot-prijs).
            </p>
            <div className="page-meta">
              <span><strong>€30</strong> all-in <s style={{ opacity: 0.5, fontWeight: 400 }}>€50</s></span>
              <span><strong>3u</strong> per zaterdag</span>
              <span><strong>Max 8</strong> kinderen</span>
              <span><strong>10-12 jaar</strong></span>
              <span><strong>Geen voorkennis</strong> nodig</span>
            </div>
            <div className="page-ctas">
              <a href="/#inschrijven" className="btn btn-primary btn-lg">
                Inschrijven →
              </a>
              <Link to="/faq" className="btn btn-ghost btn-lg">
                Veelgestelde vragen
              </Link>
            </div>
          </header>

          <div className="page-hero-img">
            <img src="/assets/projects/project-simon-says.png" alt="Simon Says memory-game" />
          </div>

          <section className="page-section">
            <h2>Wat bouwt je kind precies</h2>
            <p>
              Een werkend memory-spel: vier gekleurde LEDs, vier knoppen, een buzzer en een
              microcontroller die alles aanstuurt. Het apparaat speelt een patroon — eerst één
              kleur, dan twee, dan drie. Je kind moet het patroon nadrukken op de juiste knoppen.
              Lukt dat, wordt de reeks langer. Mis je er een, begint het opnieuw.
            </p>
            <p>
              Klein toestel ter grootte van een handpalm. Op batterijen — werkt los van de
              computer eens geprogrammeerd. Speelbaar met broers, zussen of vriendjes. Geen
              schermtijd.
            </p>
          </section>

          <section className="page-section">
            <h2>Wat leert je kind</h2>
            <ul className="check-list">
              <li>
                <strong>Een echte schakeling bouwen.</strong> Componenten op een breadboard zetten,
                draadjes verbinden volgens een schema. Eerste keer dat veel kinderen begrijpen hoe
                elektronica werkt.
              </li>
              <li>
                <strong>Programmeren met Arduino.</strong> We schrijven samen de code in C-achtige
                syntaxis. Variabelen, lussen, voorwaarden — toegepast op een tastbaar resultaat.
              </li>
              <li>
                <strong>Debuggen.</strong> Iets werkt niet meteen. We zoeken samen waarom. Fout
                draadje? Tikfout in de code? Dat zoekproces is de helft van het leren.
              </li>
              <li>
                <strong>Doorzettingsvermogen.</strong> Drie uur bezig met één project. Geen
                snelle dopamine van een YouTube-filmpje. Lang genoeg om iets te kunnen, kort
                genoeg om af te ronden.
              </li>
            </ul>
          </section>

          <section className="page-section">
            <h2>Programma — uur per uur</h2>
            <ol className="step-list">
              <li>
                <strong>Uur 1 — Hardware bouwen</strong>
                <p>
                  Welkom, kennismaking, korte uitleg. Componenten leren kennen. Eerste LED laten
                  branden — voor velen een wow-moment. Daarna alle vier de LEDs en knoppen op het
                  breadboard.
                </p>
              </li>
              <li>
                <strong>Uur 2 — Programmeren</strong>
                <p>
                  We openen de Arduino IDE samen, werken stap-voor-stap door de code. Eerst LED
                  knipperen, dan op knop reageren, dan een patroon onthouden. Per stap testen we
                  of het werkt.
                </p>
              </li>
              <li>
                <strong>Uur 3 — Spel afmaken + spelen</strong>
                <p>
                  Buzzer toevoegen voor geluidseffecten. Code finetunen. Daarna spelen tegen
                  elkaar — kortste reeks die je kan onthouden? Foto's voor mama en papa.
                </p>
              </li>
            </ol>
          </section>

          <section className="page-section">
            <h2>Wat krijgt je kind mee naar huis</h2>
            <ul className="check-list">
              <li>Het werkende Simon Says-apparaat — gemonteerd, geprogrammeerd, klaar om te spelen.</li>
              <li>Een korte handleiding om thuis verder uit te breiden (optioneel).</li>
              <li>Toegang tot de begeleidende code op GitHub — wil je kind het later aanpassen, dan kan dat.</li>
              <li>Trots. Vooral dat.</li>
            </ul>
          </section>

          <section className="page-section">
            <h2>Voor wie is dit</h2>
            <p>
              Kinderen <strong>10-12 jaar</strong> (5de en 6de leerjaar). Geen voorkennis nodig.
              Kinderen die thuis al met Scratch of Arduino bezig zijn — geen probleem, we geven
              extra uitdaging binnen het project.
            </p>
            <p>
              <strong>Vereist:</strong> eigen laptop (Windows, Mac of Linux). Geen laptop thuis?
              Mail ons, we hebben een paar reservetoestellen.
            </p>
          </section>

          <section className="page-section">
            <h2>Praktisch</h2>
            <table className="info-table">
              <tbody>
                <tr><th>Prijs</th><td>€30 all-in — pilot-prijs voor de eerste edities (normaal €50) · alle componenten + materiaal + drankje inbegrepen</td></tr>
                <tr><th>Duur</th><td>3 uur per zaterdag</td></tr>
                <tr><th>Groepsgrootte</th><td>Max 8 kinderen</td></tr>
                <tr><th>Leeftijd</th><td>10-12 jaar (5de-6de leerjaar)</td></tr>
                <tr><th>Voorkennis</th><td>Geen vereist</td></tr>
                <tr><th>Mee te brengen</th><td>Eigen laptop (we installeren samen Arduino IDE)</td></tr>
                <tr><th>Locatie</th><td>Klaslokaal in een school in de regio · exact adres bij bevestiging</td></tr>
                <tr><th>Datum</th><td>In overleg — eerste edities lopen nu, mail ons voor beschikbare zaterdagen</td></tr>
              </tbody>
            </table>
          </section>

          <section className="page-section page-section-cta">
            <h2>Klaar om in te schrijven</h2>
            <p>
              We werken in kleine groepen, dus snel zijn helpt. Laat je gegevens achter via het
              formulier op de homepage — we mailen binnen twee werkdagen met beschikbare data.
            </p>
            <div className="page-ctas">
              <a href="/#inschrijven" className="btn btn-primary btn-lg">
                Naar het inschrijfformulier →
              </a>
              <Link to="/workshops" className="btn btn-ghost btn-lg">
                Bekijk alle workshops
              </Link>
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
